import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Award, Download, Shield, TrendingUp, FileCheck, Users, CheckCircle2, Eye, Target, BarChart3, Star, Calendar, ExternalLink, ArrowRight, FileText, Mail } from 'lucide-react';
import './RatingCertificate.css';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

const RatingCertificate = () => {
  const navigate = useNavigate();
  const whyItMattersPoints = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Trustworthiness',
      description: 'Quick indicator of financial credibility'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Financial Health',
      description: 'Objective assessment of stability'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Growth Potential',
      description: 'Long-term sustainability indicator'
    }
  ];

  const certificateHighlights = [
    {
      icon: <Award className="w-5 h-5" />,
      label: 'Rating Agency',
      value: 'Recognised Independent Agency'
    },
    {
      icon: <Star className="w-5 h-5" />,
      label: 'Assigned Grade',
      value: 'BBB-',
      highlight: true
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      label: 'Issued On',
      value: 'January 2025'
    },
    {
      icon: <CheckCircle2 className="w-5 h-5" />,
      label: 'Validity Period',
      value: '12 Months'
    }
  ];

  const keyStrengths = [
    'Consistent earnings',
    'Strong capital adequacy',
    'Prudent risk management',
    'Robust governance practices'
  ];

  // Static fallback data for Behind the Scenes section
  const fallbackRatingProcess = [
    {
      number: '01',
      title: 'Business Profile Review',
      description: 'Comprehensive analysis of operations and market position'
    },
    {
      number: '02',
      title: 'Financial Metrics Assessment',
      description: 'Detailed review of audited financials and ratios'
    },
    {
      number: '03',
      title: 'Governance & Management',
      description: 'Evaluation of leadership quality and practices'
    },
    {
      number: '04',
      title: 'Industry Benchmarking',
      description: 'Comparison against peer institutions'
    },
    {
      number: '05',
      title: 'Rating Issuance',
      description: 'Final grade based on rigorous criteria'
    }
  ];

  // Behind the Scenes State
  const [behindScenesData, setBehindScenesData] = useState({
    section_tag: 'Behind the Scenes',
    title: 'How the Rating Was Determined',
    description: 'The process involves a detailed review of our business profile, financial metrics, governance practices and external environment. Analysts review audited financials, assess management quality, benchmark us against industry peers, and issue a rating based on rigorous criteria.',
    monitoring_title: 'Continuous Monitoring',
    monitoring_text: 'After issuance, the rating is monitored continuously — our commitment to maintaining high standards ensures that stakeholders stay informed about any changes in outlook or performance.',
    process_steps: fallbackRatingProcess
  });
  const [behindScenesLoading, setBehindScenesLoading] = useState(true);

  // Your Advantage Section State
  const [advantageSettings, setAdvantageSettings] = useState({
    section_tag: 'Your Advantage',
    section_title: 'Your Benefits & How to Access the Certificate',
    section_intro: 'With our credit rating certificate at hand, you gain:',
    next_steps_title: 'Next Steps',
    contact_label: 'Investor Relations',
    contact_email: 'investors@ahamhfc.com',
    update_note_label: 'Stay updated:',
    update_note_text: "We publish any rating revisions or outlook changes promptly, ensuring you're always in the know.",
    download_text: 'Click **Download the Certificate** above to obtain the PDF',
    contact_button_text: 'Contact Team'
  });
  const [advantageBenefits, setAdvantageBenefits] = useState([]);
  const [advantageNextSteps, setAdvantageNextSteps] = useState([]);
  const [advantageLoading, setAdvantageLoading] = useState(true);

  // Our Pledge Section State
  const [pledgeSettings, setPledgeSettings] = useState({
    section_tag: 'Our Pledge',
    section_title: 'Our Promise — Sustaining Financial Integrity & Growth',
    section_intro: 'At Aham, we view our credit rating not as a milestone, but as a responsibility. Each rating renewal motivates us to strengthen our financial governance, enhance transparency, and uphold investor confidence.',
    footer_text: 'We remain committed to sustaining our high credit standards and ensuring our stakeholders can always trust the integrity of our financial ecosystem.'
  });
  const [pledgePillars, setPledgePillars] = useState([]);
  const [pledgeLoading, setPledgeLoading] = useState(true);

  // Independent Assessments Section State
  const [assessmentsSettings, setAssessmentsSettings] = useState({
    section_tag: 'Independent Assessments',
    section_title: 'Credit Rating',
    section_intro: 'Aham Housing Finance Limited ("AHFL") has been independently rated by leading credit rating agencies for its borrowing programmes. The ratings reflect the Company\'s capitalisation, asset quality, liquidity position, management experience, underwriting practices, and risk management framework.'
  });
  const [assessmentCards, setAssessmentCards] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);

  // CTA Banner State
  const [ctaSettings, setCtaSettings] = useState({
    title: 'Access Our Credit Rating Certificate',
    description: 'Download the complete certificate and review our financial strength indicators.',
    button_text: 'Contact Team',
    button_link: '/contact'
  });
  const [ctaLoading, setCtaLoading] = useState(true);

  // Fallback data for Benefits
  const fallbackBenefits = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Greater Transparency',
      description: 'Clear insight into our financial credibility and operations'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Increased Assurance',
      description: 'Confidence for investment and lending decisions'
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Better Terms',
      description: 'Enhanced ability to negotiate favorable conditions'
    }
  ];

  const fallbackPillars = [
    {
      icon: <Award className="w-5 h-5" />,
      title: 'Robust Asset Quality',
      description: 'Prudent lending practices'
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      title: 'Risk-Aware Growth',
      description: 'Strategies aligned with regulations'
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      title: 'Digital Innovation',
      description: 'Real-time financial monitoring'
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: 'Ethical Governance',
      description: 'Integrity at every level'
    }
  ];

  // Banner State
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Fallback static banner data
  const fallbackBanner = {
    title: 'Our Credit Rating Certificate',
    subtitle: 'A Mark of Financial Strength. At Aham, our commitment to transparent financial performance is validated by independent rating agencies.',
    image_url: '/img/Banners/Rating-Banner.webp',
    highlights: ['BBB- Credit Rating']
  };

  // Value Section State
  const [valuePoints, setValuePoints] = useState([]);
  const [valueSettings, setValueSettings] = useState({
    section_title: 'Why A Credit Rating Certificate Matters',
    section_subtitle: "A credit rating certificate provides an objective assessment of a company's ability to meet its financial obligations and manage risk.\n\nFor our stakeholders—investors, lenders, partners—it serves as a quick indicator of trustworthiness, financial health and the potential for long-term growth.\n\nIn an increasingly complex capital market, this certificate enhances transparency, strengthens relationships and opens access to better financing terms."
  });
  const [valueLoading, setValueLoading] = useState(true);

  // Credentials Section State
  const [highlights, setHighlights] = useState([]);
  const [highlightSettings, setHighlightSettings] = useState({
    section_title: "Aham's Rating Certificate Highlights",
    cta_text: 'BBB-', // Used for Seal Grade
    cta_link: 'Credit Rating' // Used for Seal Label
  });
  const [strengths, setStrengths] = useState([]);
  const [strengthSettings, setStrengthSettings] = useState({
    section_title: 'Key Strengths Cited'
  });
  const [credentialsLoading, setCredentialsLoading] = useState(true);

  // Helper function to map icon names to components
  const getIconComponent = (iconName) => {
    const icons = {
      Shield,
      BarChart3,
      TrendingUp,
      Award,
      Star,
      CheckCircle2,
      Eye,
      Target,
      Calendar,
      FileText,
      Mail,
      // Default fallback
      Default: Star
    };
    return icons[iconName] || icons.Default;
  };

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/rating_certificate');
        const banners = response.data.data || [];

        if (banners.length > 0) {
          // Use first active banner
          // Parse highlights if it's a string
          const activeBanner = banners[0];
          const parsedBanner = {
            ...activeBanner,
            highlights: typeof activeBanner.highlights === 'string'
              ? JSON.parse(activeBanner.highlights)
              : activeBanner.highlights
          };
          setBanner(parsedBanner);
        } else {
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    const fetchValueSection = async () => {
      try {
        setValueLoading(true);
        const LOAN_TYPE = 'rating_certificate_value';
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/key-benefits/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/key-benefits/settings/${LOAN_TYPE}`)
        ]);

        if (benefitsRes.data.data) {
          setValuePoints(benefitsRes.data.data);
        }

        if (settingsRes.data.data) {
          setValueSettings(settingsRes.data.data);
        }

      } catch (error) {
        console.error('Error fetching value section data:', error);
        // Fallback to static data on error is handled by initial state
      } finally {
        setValueLoading(false);
      }
    };

    const fetchCredentialsSection = async () => {
      try {
        setCredentialsLoading(true);
        const HIGHLIGHTS_LOAN_TYPE = 'rating_certificate_highlights';
        const STRENGTHS_LOAN_TYPE = 'rating_certificate_strengths';

        const [
          highlightsRes,
          hSettingsRes,
          strengthsRes,
          sSettingsRes
        ] = await Promise.all([
          axios.get(`/api/admin/key-benefits/${HIGHLIGHTS_LOAN_TYPE}/active`),
          axios.get(`/api/admin/key-benefits/settings/${HIGHLIGHTS_LOAN_TYPE}`),
          axios.get(`/api/admin/eligibility/${STRENGTHS_LOAN_TYPE}/active`),
          axios.get(`/api/admin/eligibility/settings/${STRENGTHS_LOAN_TYPE}`)
        ]);

        if (highlightsRes.data.data) {
          setHighlights(highlightsRes.data.data);
        }
        if (hSettingsRes.data.data) {
          setHighlightSettings(hSettingsRes.data.data);
        }
        if (strengthsRes.data.data) {
          setStrengths(strengthsRes.data.data);
        }
        if (sSettingsRes.data.data) {
          setStrengthSettings(sSettingsRes.data.data);
        }

      } catch (error) {
        console.error('Error fetching credentials data:', error);
      } finally {
        setCredentialsLoading(false);
      }
    };

    const fetchBehindScenesSection = async () => {
      try {
        setBehindScenesLoading(true);
        const response = await axios.get('/api/admin/rating-behind-scenes');
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          // Parse process_steps if it's a string
          const parsedSteps = typeof data.process_steps === 'string'
            ? JSON.parse(data.process_steps)
            : (data.process_steps || fallbackRatingProcess);

          setBehindScenesData({
            section_tag: data.section_tag || 'Behind the Scenes',
            title: data.title || 'How the Rating Was Determined',
            description: data.description || '',
            monitoring_title: data.monitoring_title || 'Continuous Monitoring',
            monitoring_text: data.monitoring_text || '',
            process_steps: parsedSteps
          });
        }
      } catch (error) {
        console.error('Error fetching behind scenes data:', error);
        // Fallback to static data on error (already set in initial state)
      } finally {
        setBehindScenesLoading(false);
      }
    };

    const fetchAdvantageSection = async () => {
      try {
        setAdvantageLoading(true);
        const [settingsRes, benefitsRes, stepsRes] = await Promise.all([
          axios.get('/api/admin/rating-advantage-settings'),
          axios.get('/api/admin/key-benefits/rating_certificate_advantage/active'),
          axios.get('/api/admin/key-benefits/rating_certificate_next_steps/active')
        ]);

        if (settingsRes.data.success && settingsRes.data.data) {
          setAdvantageSettings(settingsRes.data.data);
        }
        if (benefitsRes.data.success && benefitsRes.data.data) {
          setAdvantageBenefits(benefitsRes.data.data);
        }
        if (stepsRes.data.success && stepsRes.data.data) {
          setAdvantageNextSteps(stepsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching advantage section data:', error);
      } finally {
        setAdvantageLoading(false);
      }
    };

    const fetchPledgeSection = async () => {
      try {
        setPledgeLoading(true);
        const [settingsRes, pillarsRes] = await Promise.all([
          axios.get('/api/admin/rating-pledge-settings'),
          axios.get('/api/admin/key-benefits/rating_certificate_pledge/active')
        ]);

        if (settingsRes.data.success && settingsRes.data.data) {
          setPledgeSettings(settingsRes.data.data);
        }
        if (pillarsRes.data.success && pillarsRes.data.data) {
          setPledgePillars(pillarsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching pledge section data:', error);
      } finally {
        setPledgeLoading(false);
      }
    };

    const fetchAssessmentsSection = async () => {
      try {
        setAssessmentsLoading(true);
        const [settingsRes, cardsRes] = await Promise.all([
          axios.get('/api/admin/rating-assessments-settings'),
          axios.get('/api/admin/rating-assessments-cards/active')
        ]);

        if (settingsRes.data.success && settingsRes.data.data) {
          setAssessmentsSettings(settingsRes.data.data);
        }
        if (cardsRes.data.success && cardsRes.data.data) {
          setAssessmentCards(cardsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching assessments section data:', error);
      } finally {
        setAssessmentsLoading(false);
      }
    };

    const fetchCtaSection = async () => {
      try {
        setCtaLoading(true);
        const res = await axios.get('/api/admin/cta-banner/settings/rating_certificate_cta');
        if (res.data.success && res.data.data) {
          setCtaSettings(res.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA data:', error);
      } finally {
        setCtaLoading(false);
      }
    };

    fetchBanner();
    fetchValueSection();
    fetchCredentialsSection();
    fetchBehindScenesSection();
    fetchAdvantageSection();
    fetchPledgeSection();
    fetchAssessmentsSection();
    fetchCtaSection();
  }, []);

  return (
    <div className="rating-certificate">

      <SEO
        title="Credit Rating Certificates & Financial Stability | Aham Housing Finance"
        description="View Aham Housing Finance credit rating certificates. Track our financial performance, creditworthiness, and stability ratings from leading agencies like ICRA."
        keywords="Aham Housing Finance rating certificate, Aham credit rating, Aham Housing Finance credit rating, CRISIL rating Aham Housing Finance, India Ratings Aham Housing Finance, CARE Ratings Aham Housing Finance, rating rationale Aham, credit rating certificate, housing finance company rating, bank loan rating, NCD rating, financial strength rating, rating report Aham Housing Finance, issuer rating housing finance, Aham rating document, Aham ratings, creditworthiness Aham Housing Finance, rating agency report, housing finance rating certificate, Aham financial rating"
        canonical="https://ahamhfc.com/aham-housing-finance-credit-rating-certificate"
        ogTitle="Credit Rating Certificates & Financial Stability | Aham Housing Finance"
        ogDescription="View Aham Housing Finance credit rating certificates. Track our financial performance, creditworthiness, and stability ratings from leading agencies like ICRA."
      />

      {/* Hero Banner */}
      {/* Hero Banner */}
      {/* Hero Banner */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {bannerLoading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white">Loading...</div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${banner?.image_url}')`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl text-left"
              >
                {/* Display Badge if highlights exist and match specific format or just first highlight */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-white/20">
                    <Award className="w-5 h-5" />
                    <span className="font-medium">{banner.highlights[0]}</span>
                  </div>
                )}

                <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title}
                </h1>
                <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
                  {banner?.subtitle}
                </p>

                {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6 md:mb-8"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Rating Certificate</span>
                </motion.div>

              </motion.div>
            </div>
          </>
        )}
      </section>

      {/* Section 1: Why It Matters */}
      {/* Section 1: Why It Matters */}
      <section className="why-matters-section">
        <div className="section-container">
          <div className="why-matters-layout">
            <div className="why-matters-content text-center lg:text-left">
              <span className="section-tag mx-auto lg:mx-0">Understanding the Value</span>
              <h2 className="section-title mx-auto lg:mx-0">{valueSettings?.section_title}</h2>
              <div className="why-matters-text mx-auto lg:mx-0">
                {valueSettings?.section_subtitle ? (
                  valueSettings.section_subtitle.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className={`${idx === 0 ? "why-lead" : "why-secondary"} text-center lg:text-left mx-auto lg:mx-0`}>
                      {paragraph}
                    </p>
                  ))
                ) : (
                  <p className="why-lead">Loading content...</p>
                )}
              </div>
            </div>

            <div className="why-matters-cards">
              {(valuePoints.length > 0 ? valuePoints : whyItMattersPoints).map((point, index) => {
                const IconComponent = point.icon_name ? getIconComponent(point.icon_name) : null;
                return (
                  <Card key={index} className="why-card">
                    <div className="why-card-icon">
                      {IconComponent ? <IconComponent className="w-6 h-6" /> : point.icon}
                    </div>
                    <h3 className="why-card-title">{point.title}</h3>
                    <p className="why-card-description">{point.description}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Certificate Highlights */}
      <section className="highlights-section">
        <div className="section-container">
          <div className="section-header-center text-center lg:text-left">
            <span className="section-tag mx-auto lg:mx-0">Our Credentials</span>
            <h2 className="section-title mx-auto lg:mx-0">{highlightSettings?.section_title}</h2>
          </div>

          <div className="highlights-display">
            <div className="certificate-visual">
              <div className="certificate-seal">
                <Award className="seal-icon" />
                <div className="seal-grade">{highlightSettings?.cta_text}</div>
                <div className="seal-label">{highlightSettings?.cta_link}</div>
              </div>
            </div>

            <div className="highlights-grid">
              {(highlights.length > 0 ? highlights : certificateHighlights).map((item, index) => {
                const IconComponent = item.icon_name ? getIconComponent(item.icon_name) : null;
                return (
                  <Card key={index} className={`highlight-card ${item.gradient_from === 'highlight' || item.highlight ? 'featured' : ''}`}>
                    <div className="highlight-icon">
                      {IconComponent ? (
                        <IconComponent className="w-5 h-5" />
                      ) : (
                        item.icon
                      )}
                    </div>
                    <div className="highlight-info">
                      <span className="highlight-label">{item.title || item.label}</span>
                      <span className="highlight-value">{item.description || item.value}</span>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          <Card className="strengths-card text-center lg:text-left">
            <h3 className="strengths-heading mx-auto lg:mx-0">{strengthSettings?.section_title}</h3>
            <div className="strengths-grid">
              {(strengths.length > 0 ? strengths : keyStrengths).map((strength, index) => (
                <div key={index} className="strength-item">
                  <CheckCircle2 className="strength-icon" />
                  <span>{strength.criteria ? strength.criteria : strength}</span>
                </div>
              ))}
            </div>
            <p className="strengths-note">
              <strong>What it means for you:</strong> Better confidence in our ability to serve your financing needs reliably.
            </p>
          </Card>
        </div>
      </section>

      {/* Section 3: Rating Process */}
      <section className="process-section-rating">
        <div className="section-container">
          <div className="section-header-center text-center lg:text-left">
            <span className="section-tag mx-auto lg:mx-0">{behindScenesData.section_tag}</span>
            <h2 className="section-title mx-auto lg:mx-0">{behindScenesData.title}</h2>
            <p className="section-description mx-auto lg:mx-0">
              {behindScenesData.description}
            </p>
          </div>

          {behindScenesLoading ? (
            <div className="animate-pulse h-64 bg-slate-100 rounded-xl mb-6"></div>
          ) : (
            <div className="process-timeline-rating">
              {behindScenesData.process_steps
                .filter(step => step.is_active !== false)
                .map((step, index) => (
                  <div key={index} className="timeline-step-rating">
                    <div className="step-number-rating">{step.number}</div>
                    <div className="step-content-rating">
                      <h3 className="step-title-rating">{step.title}</h3>
                      <p className="step-description-rating">{step.description}</p>
                    </div>
                    {index < behindScenesData.process_steps.filter(s => s.is_active !== false).length - 1 && (
                      <div className="step-connector-rating"></div>
                    )}
                  </div>
                ))}
            </div>
          )}

          <Card className="monitoring-card">
            <Shield className="monitoring-icon" />
            <div className="monitoring-content">
              <h3 className="monitoring-title">{behindScenesData.monitoring_title}</h3>
              <p className="monitoring-text">
                {behindScenesData.monitoring_text}
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Section 4: Benefits & Next Steps */}
      <section className="benefits-section-rating">
        <div className="section-container">
          <div className="benefits-layout">
            <div className="benefits-content text-center lg:text-left">
              <span className="section-tag mx-auto lg:mx-0">{advantageSettings.section_tag}</span>
              <h2 className="section-title mx-auto lg:mx-0">{advantageSettings.section_title}</h2>
              <p className="benefits-intro mx-auto lg:mx-0">
                {advantageSettings.section_intro}
              </p>

              <div className="benefits-cards">
                {(advantageBenefits.length > 0 ? advantageBenefits : fallbackBenefits).map((benefit, index) => {
                  const IconComponent = benefit.icon_name ? getIconComponent(benefit.icon_name) : null;
                  return (
                    <Card key={index} className="benefit-card-rating">
                      <div className="benefit-icon-rating">
                        {IconComponent ? <IconComponent className="w-6 h-6" /> : benefit.icon}
                      </div>
                      <div className="benefit-text">
                        <h3 className="benefit-title-rating">{benefit.title}</h3>
                        <p className="benefit-description-rating">{benefit.description}</p>
                      </div>
                    </Card>
                  )
                })}
              </div>
            </div>

            <Card className="next-steps-card">
              <h3 className="next-steps-heading">{advantageSettings.next_steps_title}</h3>
              <div className="next-steps-list">
                {advantageNextSteps.length > 0 ? (
                  advantageNextSteps.map((step, index) => {
                    // Parse for bold text - simple regex replacement
                    const parts = step.description.split(/(\*\*.*?\*\*)/g);
                    return (
                      <div key={index} className="next-step-item">
                        <div className="step-number-badge">{index + 1}</div>
                        <p>
                          {parts.map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={i}>{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div className="next-step-item">
                      <div className="step-number-badge">1</div>
                      <p>Click <strong>Download the Certificate</strong> above to obtain the PDF</p>
                    </div>
                    <div className="next-step-item">
                      <div className="step-number-badge">2</div>
                      <p>Review the rating details and highlights</p>
                    </div>
                    <div className="next-step-item">
                      <div className="step-number-badge">3</div>
                      <p>For queries, contact our Investor Relations team</p>
                    </div>
                  </>
                )}

              </div>
              <div className="contact-info">
                <Users className="contact-icon" />
                <div>
                  <p className="contact-label">{advantageSettings.contact_label}</p>
                  <a href={`mailto:${advantageSettings.contact_email}`} className="contact-link">{advantageSettings.contact_email}</a>
                </div>
              </div>
              <p className="update-note">
                <strong>{advantageSettings.update_note_label}</strong> {advantageSettings.update_note_text}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 5: Commitment */}
      <section className="commitment-section">
        <div className="section-container">
          <div className="commitment-header text-center lg:text-left">
            <span className="section-tag mx-auto lg:mx-0">{pledgeSettings.section_tag}</span>
            <h2 className="section-title-white mx-auto lg:mx-0">{pledgeSettings.section_title}</h2>
            <p className="commitment-intro mx-auto lg:mx-0">
              {pledgeSettings.section_intro}
            </p>
          </div>

          <div className="commitment-pillars">
            {(pledgePillars.length > 0 ? pledgePillars : fallbackPillars).map((pillar, index) => {
              const IconComponent = pillar.icon_name ? getIconComponent(pillar.icon_name) : null;
              return (
                <Card key={index} className="pillar-card">
                  <div className="pillar-icon">
                    {IconComponent ? <IconComponent className="w-5 h-5" /> : pillar.icon}
                  </div>
                  <h3 className="pillar-title">{pillar.title}</h3>
                  <p className="pillar-description">{pillar.description}</p>
                </Card>
              )
            })}
          </div>

          <p className="commitment-footer">
            {pledgeSettings.footer_text}
          </p>
        </div>
      </section>

      {/* Credit Rating Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              {assessmentsSettings.section_tag}
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              {assessmentsSettings.section_title}
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {assessmentsSettings.section_intro}
            </p>
          </motion.div>

          {/* Rating Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {assessmentCards.map((card, index) => {
              const IconComponent = card.icon_name ? getIconComponent(card.icon_name) : null;
              // Map gradient names to tailwind classes if needed, or use them directly if stored as full class parts
              // The seed stores 'blue-500' etc. We need `from-${color}`.
              // Assuming the seed data is like 'blue-500', we construct the classes.
              const fromColor = `from-${card.gradient_from}`;
              const toColor = `to-${card.gradient_to}`;
              const borderColor = `hover:border-${card.gradient_from.split('-')[0]}-200`;
              const bgColor = `bg-gradient-to-bl from-${card.gradient_from.split('-')[0]}-50`;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  onClick={() => window.open(card.pdf_link, '_blank')}
                  className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 ${borderColor} cursor-pointer relative overflow-hidden`}
                >
                  <div className={`absolute top-0 right-0 w-32 h-32 ${bgColor} to-transparent rounded-bl-full`}></div>
                  <div className="relative z-10 text-center">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${fromColor} ${toColor} flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                      {IconComponent ? <IconComponent className="h-8 w-8 text-white" /> : <Award className="h-8 w-8 text-white" />}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{card.agency_name}</h3>
                    {card.agency_subtext && <p className="text-xs text-gray-500 mb-3">{card.agency_subtext}</p>}
                    <div className="space-y-2 mb-4">
                      {card.instrument && <p className="text-sm text-gray-600"><span className="font-semibold">Instrument:</span> {card.instrument}</p>}
                      <div className="flex items-center gap-2">
                        {card.rating_value && <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold">{card.rating_value}</span>}
                        {card.rating_outlook && <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">{card.rating_outlook}</span>}
                      </div>
                      {card.rating_date && <p className="text-sm text-gray-500"><Calendar className="w-4 h-4 inline mr-1" />{card.rating_date}</p>}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {card.description}
                    </p>
                    <div className={`flex items-center justify-center text-${card.gradient_from.split('-')[0]}-600 font-semibold group-hover:translate-x-2 transition-transform duration-300`}>
                      <span>View Details</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center text-sm text-gray-500 mt-10 italic max-w-3xl mx-auto"
          >
            For detailed rating rationale, methodologies, and assumptions, please click on the respective card to view the official rating communication.
          </motion.p>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="rating-cta">
        <div className="rating-cta-container">
          <Award className="cta-award-icon" />
          <h2 className="cta-rating-title">{ctaSettings.title}</h2>
          <p className="cta-rating-text">
            {ctaSettings.description}
          </p>
          <div className="cta-rating-actions">
           
            <Button variant="outline" className="cta-rating-secondary"
              onClick={() => navigate(ctaSettings.button_link || '/contact')}>
              {ctaSettings.button_text}
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default RatingCertificate;